import React, { useEffect, useState } from 'react'
import { Card, Table, Button, Select, InputNumber, Form, Row, Col, Divider, Space, message } from 'antd'
import useExercisesManagement from '../hooks/useExercisesManagement'
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons'

const { Option } = Select

const ExercisesManagement = ({ value = [], onChange }) => {
  const [form] = Form.useForm()
  const { exercises, loading } = useExercisesManagement()
  const [selectedExercises, setSelectedExercises] = useState(value)

  useEffect(() => {
    if (value && value.length > 0) {
      setSelectedExercises(value)
    }
  }, [value])

  const handleAddExercise = () => {
    form.validateFields().then((values) => {
      const selectedExercise = exercises.find((ex) => ex.id === values.exerciseId)

      const newExercise = {
        id: values.exerciseId,
        name: selectedExercise?.name || '',
        description: selectedExercise?.description || '',
        frequency: values.frequency,
        duration: values.duration,
        repetitions: values.repetitions,
        sets: values.sets,
        notes: values.notes || '',
        adherence: 0
      }

      const updatedExercises = [...selectedExercises, newExercise]
      setSelectedExercises(updatedExercises)
      onChange(updatedExercises)

      form.resetFields()
      message.success('Exercise added successfully')
    })
  }

  const handleRemoveExercise = (exerciseId) => {
    const updatedExercises = selectedExercises.filter((ex) => ex.id !== exerciseId)
    setSelectedExercises(updatedExercises)
    onChange(updatedExercises)
    message.success('Exercise removed successfully')
  }

  const columns = [
    {
      title: 'Exercise',
      dataIndex: 'name',
      key: 'name',
      width: '20%'
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: '25%'
    },
    {
      title: 'Frequency',
      dataIndex: 'frequency',
      key: 'frequency',
      width: '10%'
    },
    {
      title: 'Duration',
      dataIndex: 'duration',
      key: 'duration',
      width: '10%',
      render: (duration) => `${duration} minutes`
    },
    {
      title: 'Sets',
      dataIndex: 'sets',
      key: 'sets',
      width: '5%'
    },
    {
      title: 'Repetitions',
      dataIndex: 'repetitions',
      key: 'repetitions',
      width: '10%'
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      width: '15%'
    },
    {
      title: 'Action',
      key: 'action',
      width: '5%',
      render: (_, record) => (
        <Button icon={<DeleteOutlined />} danger type='link' onClick={() => handleRemoveExercise(record.id)} />
      )
    }
  ]

  return (
    <Card title='Home Exercises & Activities' bordered={false}>
      <Form form={form} layout='vertical'>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name='exerciseId'
              label='Exercise'
              rules={[{ required: true, message: 'Please select an exercise' }]}
            >
              <Select placeholder='Select an exercise' loading={loading} showSearch optionFilterProp='children'>
                {exercises.map((exercise) => (
                  <Option key={exercise.id} value={exercise.id}>
                    {exercise.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item
              name='frequency'
              label='Frequency'
              rules={[{ required: true, message: 'Please select frequency' }]}
            >
              <Select placeholder='Select frequency'>
                <Option value='Daily'>Daily</Option>
                <Option value='Twice a day'>Twice a day</Option>
                <Option value='Every other day'>Every other day</Option>
                <Option value='3 times a week'>3 times a week</Option>
                <Option value='2 times a week'>2 times a week</Option>
                <Option value='Weekly'>Weekly</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={3}>
            <Form.Item
              name='duration'
              label='Duration (mins)'
              rules={[{ required: true, message: 'Please enter duration' }]}
            >
              <InputNumber min={1} max={120} placeholder='Duration' style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={3}>
            <Form.Item name='sets' label='Sets' rules={[{ required: true, message: 'Please enter sets' }]}>
              <InputNumber min={1} max={10} placeholder='Sets' style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={3}>
            <Form.Item
              name='repetitions'
              label='Repetitions'
              rules={[{ required: true, message: 'Please enter repetitions' }]}
            >
              <InputNumber min={1} max={30} placeholder='Reps' style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={3}>
            <Form.Item name='notes' label='Notes'>
              <Select placeholder='Add notes'>
                <Option value='Gentle movements only'>Gentle movements only</Option>
                <Option value='Use support if needed'>Use support if needed</Option>
                <Option value='Stop if painful'>Stop if painful</Option>
                <Option value='Increase gradually'>Increase gradually</Option>
                <Option value='With supervision'>With supervision</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24} style={{ textAlign: 'right' }}>
            <Button type='primary' onClick={handleAddExercise} icon={<PlusOutlined />}>
              Add Exercise
            </Button>
          </Col>
        </Row>
      </Form>

      <Divider />

      <Table columns={columns} dataSource={selectedExercises} rowKey='id' pagination={false} scroll={{ y: 300 }} />

      {selectedExercises.length === 0 && (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          No exercises added yet. Use the form above to add exercises to the rehabilitation plan.
        </div>
      )}
    </Card>
  )
}

export default ExercisesManagement
